import { de } from '../i18n/de';

/**
 * The layer below SafeModeBoundary: what shows when React never mounts at all.
 *
 * No React, no components, no stylesheet — plain DOM built by hand, so the only
 * things that have to work are the language itself and `speechSynthesis`. It uses
 * a `<textarea>` and therefore the iOS system keyboard, which is a worse
 * experience than our own grid but needs none of our code to be working.
 *
 * If even this fails, the static markup in index.html is still on screen pointing
 * at the printed kit (ADR-007), and that is the real floor.
 */
export function renderBootFloor(root: HTMLElement, reason: unknown): void {
  console.error('[boot-floor] app failed to start', reason);

  root.textContent = '';

  const wrap = document.createElement('div');
  wrap.setAttribute('data-testid', 'boot-floor');
  wrap.style.cssText =
    'display:flex;flex-direction:column;gap:12px;height:100%;padding:16px;' +
    'background:#fff;color:#000;font:400 20px -apple-system,Helvetica,Arial,sans-serif';

  const title = document.createElement('strong');
  title.textContent = de.safeModeTitle;
  title.style.fontSize = '24px';

  const input = document.createElement('textarea');
  input.setAttribute('aria-label', de.speak);
  input.style.cssText =
    'flex:1 1 auto;width:100%;padding:12px;font-size:48px;line-height:1.2;' +
    'border:3px solid #000;border-radius:8px;background:#fff;color:#000';

  const speak = document.createElement('button');
  speak.type = 'button';
  speak.textContent = de.speak;
  speak.style.cssText =
    'height:96px;font-size:32px;font-weight:700;background:#000;color:#fff;' +
    'border:3px solid #000;border-radius:8px';
  speak.addEventListener('click', () => {
    const text = input.value.trim();
    if (!text) return;
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.error('[boot-floor] speechSynthesis unavailable', err);
    }
  });

  wrap.append(title, input, speak);
  root.append(wrap);
}
